import React, { FC, useState } from "react"
import { DatePicker, Form, Input, Radio, Select, Switch } from "antd"
import { DefaultLinksRouting, UploadComponent } from "components"

import { observer } from "mobx-react"
import moment from "moment"
import { useStore } from "stores/utils/use-store"
import { notifications } from "notifications"
import { gameNames } from "abstract/constants/game-ids"
import {
  ConfigItemByType,
  InGameBannerType,
  NameItem,
  userTypes,
  windowTypesType,
} from "domains/managingJson/store/types"
import { SubmitCancelButtons } from "components"
import GeoPickerElement from "components/geoPicker"

export const BannerEdit: FC<{ editType: "create" | "edit"; onClose: () => void }> = observer(({ editType, onClose }) => {
  const {
    dataStore: {
      managingJsonStore,
      managingJsonStore: { selectedElement },
    },
  } = useStore()

  const data = selectedElement.data as ConfigItemByType["inGameBanners"]
  const name = selectedElement.name

  const [image, setImage] = useState(editType === "edit" ? data?.image || "" : "")
  const [ignoreAdsHiding, setignoreAdsHiding] = useState(editType === "edit" ? data?.ignoreAdsHiding || false : false)
  const [clickable, setclickable] = useState<boolean>(editType === "edit" ? data?.clickable || false : true)

  const [link] = useState(editType === "edit" ? data?.link || undefined : undefined)
  const [userType, setuserType] = useState(editType === "edit" ? data?.userType || "ALL" : "ALL")
  const [windows, setwindows] = useState(editType === "edit" ? data?.windows || [] : [])

  const onFinishHandler = async(e: InGameBannerType) => {
    if (image?.length === 0) {
      return notifications.error("Please select banner image")
    }
    const data = {
      ...e,
      image,
      windows: windows?.length > 0 ? windows : undefined,
      userType,
      countries: e?.countries,
      gameIds: e?.gameIds && e?.gameIds.length > 0 ? String(e?.gameIds).split(",") : undefined,
      startDate: e?.startDate ? e?.startDate : undefined,
      endDate: e?.endDate ? e?.endDate : undefined,
      ignoreAdsHiding: e?.ignoreAdsHiding ? e?.ignoreAdsHiding : undefined,
      clickable: e?.clickable,
    }

    if (e.name !== name && editType === "edit") {
      const dataToBack: NameItem = {
        oldName: name || "",
        newName: e.name,
        type: "adsBanners",
      }
      await managingJsonStore.updateName(dataToBack)
    }

    managingJsonStore.funcAfterSuccess = async() => {
      onClose()
      await managingJsonStore.getData()
    }
    editType === "edit"
      ? await managingJsonStore.update({
        name: e.name || "",
        type: "adsBanners",
        data,
      })
      : await managingJsonStore.create({
        name: e.name || "",
        type: "adsBanners",
        data,
      })
  }
  const onFinishFailedHandler = (e: any) => {
    console.log(e)
  }

  const changeSwitcherWindow = (windowName: windowTypesType) => {
    if (windows?.includes(windowName)) {
      setwindows(windows.filter(item => item !== windowName))
    } else {
      const newWindows = windows && [...windows]
      newWindows?.push(windowName)
      setwindows(newWindows)
    }
  }

  return (
    <Form onFinish={onFinishHandler} onFinishFailed={onFinishFailedHandler} id="createBanner">
      <Form.Item
        label="Name"
        name="name"
        initialValue={name}
        rules={[
          {
            required: true,
            message: "Please input name!",
          },
        ]}
        style={{ width: "100%" }}
      >
        <Input />
      </Form.Item>
      <GeoPickerElement label="Countries" name="countries" initialValue={data?.countries} />
      <Form.Item
        label="Ignore ads hiding"
        style={{ marginBottom: "14px" }}
        initialValue={ignoreAdsHiding}
        name="ignoreAdsHiding"
      >
        <Switch checked={ignoreAdsHiding} onChange={() => setignoreAdsHiding(!ignoreAdsHiding)} />
      </Form.Item>
      <Form.Item label="Game Ids" name="gameIds" initialValue={data?.gameIds || undefined}>
        <Select mode="multiple">
          {Object.entries(gameNames).map(el => (
            <Select.Option key={el[0]} value={el[0]}>{el[1]}</Select.Option>
          ))}
        </Select>
      </Form.Item>
      <Form.Item required label="Clickable" name="clickable" initialValue={clickable}>
        <Radio.Group
          value={userType}
          defaultValue={undefined}
          onChange={e => {
            setclickable(e.target.value)
          }}
        >
          <Radio value={true}>Clickable button</Radio>
          <Radio value={false}>QR</Radio>
        </Radio.Group>
      </Form.Item>

      {clickable && <DefaultLinksRouting link={link || ""} formItemName="link" notRequired />}

      <Form.Item
        required
        label="User Type"
        name="userType"
        initialValue={data?.userType || "ALL"}
      >
        <Radio.Group
          value={userType}
          defaultValue={undefined}
          onChange={e => {
            setuserType(e.target.value)
          }}
        >
          {userTypes.map(item => (
            <Radio value={item} key={item}>{item}</Radio>
          ))}
        </Radio.Group>
      </Form.Item>

      <Form.Item
        label="Main screen"
        style={{ marginBottom: "14px" }}
        initialValue={windows?.includes("MAIN_SCREEN")}
      >
        <Switch
          checked={windows?.includes("MAIN_SCREEN")}
          onChange={() => changeSwitcherWindow("MAIN_SCREEN")}
        />
      </Form.Item>
      <Form.Item
        label="Startgame"
        style={{ marginBottom: "14px" }}
        initialValue={windows?.includes("START_GAME")}
      >
        <Switch
          checked={windows?.includes("START_GAME")}
          onChange={() => changeSwitcherWindow("START_GAME")}
        />
      </Form.Item>
      <Form.Item
        label="Endgame"
        style={{ marginBottom: "14px" }}
        initialValue={windows?.includes("END_GAME")}
      >
        <Switch
          checked={windows?.includes("END_GAME")}
          onChange={() => changeSwitcherWindow("END_GAME")}
        />
      </Form.Item>

      <Form.Item
        label="Launch game"
        style={{ marginBottom: "14px" }}
        initialValue={windows?.includes("LAUNCH_GAME")}
      >
        <Switch
          checked={windows?.includes("LAUNCH_GAME")}
          onChange={() => changeSwitcherWindow("LAUNCH_GAME")}
        />
      </Form.Item>
      <Form.Item
        label="Game summary"
        style={{ marginBottom: "14px" }}
        initialValue={windows?.includes("GAME_SUMMURY")}
      >
        <Switch
          checked={windows?.includes("GAME_SUMMURY")}
          onChange={() => changeSwitcherWindow("GAME_SUMMURY")}
        />
      </Form.Item>

      <Form.Item
        label="Second monitor"
        style={{ marginBottom: "14px" }}
        initialValue={windows?.includes("SECOND_MONITOR")}
      >
        <Switch
          checked={windows?.includes("SECOND_MONITOR")}
          onChange={() => changeSwitcherWindow("SECOND_MONITOR")}
        />
      </Form.Item>
      <div style={{ display: "flex", gap: "20px" }}>
        <Form.Item
          label="Start Date"
          name="startDate"
          initialValue={data?.startDate ? moment(data?.startDate as Date) : ""}
          style={{ width: "100%" }}
        >
          <DatePicker
            showTime
            format="YYYY-MM-DD HH:mm:ss"
            placeholder="Select Time"
            style={{ width: "100%" }}
          />
        </Form.Item>
        <Form.Item
          label="End Date"
          name="endDate"
          initialValue={data?.endDate ? moment(data?.endDate as Date) : ""}
          style={{ width: "100%" }}
        >
          <DatePicker
            showTime
            format="YYYY-MM-DD HH:mm:ss"
            placeholder="Select Time"
            style={{ width: "100%" }}
          />
        </Form.Item>
      </div>
      <UploadComponent
        image={image}
        setImage={setImage}
        uploadStyle={{ className: "avatar-uploader", listType: "picture" }}
        imgStyle={{ width: "100%", textAlign: "center" }}
        placeholder="Upload item image"
        placeHolderStyle={{ display: "flex", flexDirection: "row" }}
      />
      <SubmitCancelButtons onClose={onClose} isLoading={managingJsonStore.isLoading} />
    </Form>
  )
})
